import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { SignupFormClusterComponent } from '../../components/signup-form-cluster/signup-form-cluster.component';
import { HandoffInterstitialComponent } from '../../components/handoff-interstitial/handoff-interstitial.component';
import { TenantSignupService } from '../../services/tenant-signup.service';
import { PublicTermsService } from '../../services/public-terms.service';
import { CaptchaService, SignupCaptchaToken } from '../../services/captcha.service';
import { ActiveTerm, SignupErrorEnvelope, SignupRequest, SignupResponse } from '../../services/tenant-signup.types';
import { TermFullTextDialogComponent } from './term-full-text-dialog.component';
import { environment } from '../../../environments/environment';
import { normalizeVatId, vatIdValidator } from '../../../../../shared/forms/vat-id';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatDialogModule,
    MatSnackBarModule,
    TranslocoModule,
    SignupFormClusterComponent,
    HandoffInterstitialComponent,
  ],
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.css'],
})
export class SignupComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly signupService = inject(TenantSignupService);
  private readonly publicTermsService = inject(PublicTermsService);
  private readonly captchaService = inject(CaptchaService);
  // Story 54.4 — true while the captcha is being solved (aria-busy + the
  // polite status line).
  captchaSolving = false;
  private readonly snackBar = inject(MatSnackBar);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly transloco = inject(TranslocoService);
  private readonly dialog = inject(MatDialog);

  protected readonly form: FormGroup;
  protected submitting = false;
  protected passwordVisible = false;
  protected handoffResponse: SignupResponse | null = null;
  protected activeTerms: ActiveTerm[] = [];
  protected termsLoadError = false;
  protected termsLoading = true;
  protected termsMissingAcceptance = false;
  // Map<termId, 'accepted' | 'declined'>. Terms start undecided (not in map).
  private readonly termDecisions = new Map<string, 'accepted' | 'declined'>();

  // Transloco keys (resolved via the `transloco` pipe in the template) — a live
  // language switch re-renders the chips. Story 25.6.
  protected readonly trustChips: string[] = [
    'common.trustChips.euHosted',
    'common.trustChips.openSource',
    'common.trustChips.specCorrect',
    'common.trustChips.wcag',
  ];

  @ViewChild('submitButton', { read: ElementRef })
  protected submitButton?: ElementRef<HTMLButtonElement>;

  constructor() {
    this.form = this.fb.group({
      operatorEmail: ['', [Validators.required, Validators.email]],
      operatorPassword: ['', [Validators.required, Validators.minLength(12)]],
      companyName: ['', [Validators.required]],
      tenantDisplayName: ['', [Validators.required]],
      contactName: ['', [Validators.required]],
      contactEmail: ['', [Validators.required, Validators.email]],
      contactPhone: [''],
      addressStreet: ['', [Validators.required]],
      addressCity: ['', [Validators.required]],
      addressPostalCode: ['', [Validators.required]],
      addressCountry: ['', [Validators.required, Validators.pattern(/^[A-Z]{2}$/)]],
      // Story 60.1 — optional; the server's utils.NormalizeVatId rule, mirrored.
      vatId: ['', [vatIdValidator]],
    });
  }

  ngOnInit(): void {
    this.publicTermsService.getActiveTerms().pipe(
      catchError(() => {
        this.termsLoadError = true;
        return of([] as ActiveTerm[]);
      }),
    ).subscribe(terms => {
      this.activeTerms = terms;
      this.termsLoading = false;
    });
  }

  protected get operatorEmail() {
    return this.form.controls['operatorEmail'];
  }
  protected get operatorPassword() {
    return this.form.controls['operatorPassword'];
  }
  protected get companyName() {
    return this.form.controls['companyName'];
  }
  protected get tenantDisplayName() {
    return this.form.controls['tenantDisplayName'];
  }
  protected get contactName() {
    return this.form.controls['contactName'];
  }
  protected get contactEmail() {
    return this.form.controls['contactEmail'];
  }
  protected get contactPhone() {
    return this.form.controls['contactPhone'];
  }
  protected get addressStreet() {
    return this.form.controls['addressStreet'];
  }
  protected get addressCity() {
    return this.form.controls['addressCity'];
  }
  protected get addressPostalCode() {
    return this.form.controls['addressPostalCode'];
  }
  protected get addressCountry() {
    return this.form.controls['addressCountry'];
  }
  protected get vatId() {
    return this.form.controls['vatId'];
  }

  protected get adminSigninUrl(): string {
    return environment.adminSigninBaseUrl;
  }

  protected togglePassword(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  protected isTermAccepted(termId: string): boolean {
    return this.termDecisions.get(termId) === 'accepted';
  }

  protected toggleTerm(termId: string): void {
    const current = this.termDecisions.get(termId);
    this.termDecisions.set(termId, current === 'accepted' ? 'declined' : 'accepted');
    this.termsMissingAcceptance = false;
  }

  protected openFullText(term: ActiveTerm): void {
    this.dialog.open(TermFullTextDialogComponent, {
      data: { title: term.number, fullText: term.fullText },
      maxWidth: '720px',
      width: '90vw',
    });
  }

  protected async submit(): Promise<void> {
    if (this.submitting) {
      return;
    }
    this.trimStringFields();
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.focusFirstInvalid();
      return;
    }
    if (this.termsLoading || this.termsLoadError) {
      return;
    }

    // Validate that all mandatory terms are accepted.
    const missingMandatory = this.activeTerms
      .filter(t => t.kind === 'term')
      .some(t => this.termDecisions.get(t.id) !== 'accepted');
    if (missingMandatory) {
      this.termsMissingAcceptance = true;
      return;
    }
    this.termsMissingAcceptance = false;

    this.submitting = true;
    // Story 54.4 — the captcha is solved here, at submit, every time.
    let captcha: SignupCaptchaToken | null;
    this.captchaSolving = true;
    try {
      captcha = await this.captchaService.solve();
    } catch {
      this.submitting = false;
      this.openRetrySnack(this.transloco.translate('signup.backend.captchaFailed'));
      return;
    } finally {
      this.captchaSolving = false;
    }

    const trimmed = this.trimmedRequest(captcha);
    this.signupService.signup(trimmed).subscribe({
      next: (resp: SignupResponse) => {
        this.submitting = false;
        this.handoffResponse = resp;
      },
      error: (err: HttpErrorResponse) => {
        this.submitting = false;
        this.applyErrorEnvelope(err);
      },
    });
  }

  private trimStringFields(): void {
    const stringFields = [
      'operatorEmail',
      'companyName',
      'tenantDisplayName',
      'contactName',
      'contactEmail',
      'contactPhone',
      'addressStreet',
      'addressCity',
      'addressPostalCode',
    ];
    for (const name of stringFields) {
      const ctrl = this.form.controls[name];
      const raw = (ctrl.value as string | null) ?? '';
      const trimmed = raw.trim();
      if (raw !== trimmed) {
        ctrl.setValue(trimmed);
      }
    }
    const countryCtrl = this.form.controls['addressCountry'];
    const country = ((countryCtrl.value as string | null) ?? '').trim().toUpperCase();
    if (country !== countryCtrl.value) {
      countryCtrl.setValue(country);
    }
  }

  private trimmedRequest(captcha: SignupCaptchaToken | null): SignupRequest {
    const raw = this.form.value as Record<string, string | null>;
    const trim = (k: string): string => ((raw[k] as string) ?? '').trim();
    const phone = (raw['contactPhone'] as string | null) ?? '';
    return {
      operatorEmail: trim('operatorEmail'),
      operatorPassword: (raw['operatorPassword'] as string) ?? '',
      companyName: trim('companyName'),
      tenantDisplayName: trim('tenantDisplayName'),
      contactEmail: trim('contactEmail'),
      contactName: trim('contactName'),
      contactPhone: phone.trim() === '' ? null : phone.trim(),
      addressStreet: trim('addressStreet'),
      addressCity: trim('addressCity'),
      addressPostalCode: trim('addressPostalCode'),
      addressCountry: trim('addressCountry').toUpperCase(),
      vatId: normalizeVatId(raw['vatId']) || null,
      termDecisions: this.activeTerms.map(term => ({
        termId: term.id,
        decision: this.termDecisions.get(term.id) ?? 'declined',
      })),
      // The selected provider's field only (AC6); none for `none`.
      ...(captcha ? {[captcha.field]: captcha.value} : {}),
    };
  }

  private applyErrorEnvelope(err: HttpErrorResponse): void {
    const envelope = (err.error ?? {}) as Partial<SignupErrorEnvelope>;
    const code = envelope.error ?? '';
    const description = (envelope.errorDescription ?? '').toLowerCase();

    switch (code) {
      case 'invalid_email': {
        const msg = this.transloco.translate('signup.errors.emailInvalid');
        const onContact = description.includes('contactemail');
        const onOperator = description.includes('operatoremail');
        if (onContact && !onOperator) {
          this.setBackendError('contactEmail', msg);
        } else if (onOperator && !onContact) {
          this.setBackendError('operatorEmail', msg);
        } else {
          this.setBackendError('operatorEmail', msg);
          this.setBackendError('contactEmail', msg);
        }
        this.focusFirstWithBackendError(['operatorEmail', 'contactEmail']);
        break;
      }
      case 'weak_password':
        this.setBackendError(
          'operatorPassword',
          this.transloco.translate('signup.errors.passwordMinLength'),
        );
        this.focusFirstWithBackendError(['operatorPassword']);
        break;
      case 'captcha_failed':
        this.openRetrySnack(this.transloco.translate('signup.backend.captchaFailed'));
        break;
      case 'temporarily_unavailable':
        this.openRetrySnack(this.transloco.translate('signup.backend.temporarilyUnavailable'));
        break;
      case 'invalid_request':
        // Story 60.1 — the one field-addressable invalid_request: the server
        // names vatId in the description when it rejects the VAT id's shape.
        if (description.includes('vatid')) {
          this.setBackendError('vatId', this.transloco.translate('signup.errors.vatIdInvalid'));
          this.focusFirstWithBackendError(['vatId']);
          break;
        }
        this.openRetrySnack(this.transloco.translate('signup.backend.generic'));
        break;
      case 'server_error':
      default:
        this.openRetrySnack(this.transloco.translate('signup.backend.generic'));
        break;
    }
  }

  private setBackendError(controlName: string, message: string): void {
    const control = this.form.controls[controlName];
    if (!control) {
      return;
    }
    control.setErrors({ ...(control.errors ?? {}), backend: message });
    control.markAsTouched();
  }

  private openRetrySnack(message: string): void {
    this.snackBar
      .open(message, this.transloco.translate('signup.retry'), {
        duration: 8000,
        panelClass: ['mat-toolbar', 'mat-warn'],
      })
      .onAction()
      .subscribe(() => void this.submit());
  }

  private focusFirstInvalid(): void {
    const el: HTMLElement | null =
      this.host.nativeElement.querySelector('.ng-invalid[formcontrolname]') ??
      this.host.nativeElement.querySelector('.mat-mdc-form-field-error-wrapper input');
    if (el) {
      (el as HTMLInputElement).focus();
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private focusFirstWithBackendError(candidates: string[]): void {
    for (const name of candidates) {
      const control = this.form.controls[name];
      if (control?.errors?.['backend']) {
        const el = this.host.nativeElement.querySelector(
          `[formcontrolname="${name}"]`,
        ) as HTMLInputElement | null;
        if (el) {
          el.focus();
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
    }
  }
}
